/**
 * CreateFirmaModal.jsx — "Yeni Firma" popup'ı
 *
 * Fuara İLK KEZ katılan bir firma için, stand oluşturma/düzenleme akışını
 * bırakmadan firma kaydı açar. Form kasıtlı olarak küçük: POST /firma/create
 * yalnızca beş alan alıyor ve bunların ikisi (firmaNation/countryId) birbirini
 * belirliyor — bkz. docs/guides/FE-Firma-create-match-search-guide.md bölüm 1.
 *
 * İki adımlı bir iş yapıyor:
 *   1) POST /firma/create      — firma satırı (hiçbir fuara bağlı değil)
 *   2) POST /firma/esle        — opsiyonel: firmayı BU FUARA bir rolle bağlar
 * Adım 2 opsiyonel çünkü stand tarafındaki atama (firma_to_stand) ayrı bir şey;
 * ama fuara bağlanmayan firma, fuarın firma listelerinde HİÇ görünmez (o listeler
 * firma_to_fuar'a join'liyor), o yüzden varsayılan olarak işaretli geliyor.
 *
 * ⚠️ /create yeni id'yi DÖNMÜYOR; id'yi geri okuma zinciri servis içinde
 * (firmaAramaService.createFirma). Nadiren id çözülemez — o durumda firma
 * OLUŞMUŞTUR, kullanıcıyı tekrar denemeye göndermiyoruz (mükerrer ad kontrolü de
 * yok, ikinci deneme ikinci firma demek).
 *
 * Backlog #197 (2026-10-10): Vergi Numarası alanı. /create artık taxNumber alıyor ve yeni id'yi
 * dönüyor; doğrulama Components/Firma/vergiNo.js'te (backend'le aynı kural).
 */
import React, { useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Row, Col,
  FormGroup, Label, Input, Button, Alert, Spinner, FormText,
} from "reactstrap";
import { Plus, Building2 } from "lucide-react";
import {
  createFirma, esleFirmaToFuar,
  TURKIYE_COUNTRY_ID, UNKNOWN_COUNTRY_ID, FIRMA_NATION, FIRMA_TYPE, FIRMA_NAME_MAX,
} from "../../services/firmaAramaService";
import { vergiNoHatasi, vergiNoTemizle, VERGI_NO_MAX } from "./vergiNo";
import UlkeSecici from "./UlkeSecici";
import { uyrukDegisinceUlke } from "./firmaUyruk";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const ROLE_OPTIONS = [
  { id: FIRMA_TYPE.KATILIMCI, get label() { return i18n.t("components:yeniFirma.katilimci"); } },
  { id: FIRMA_TYPE.DEKORATOR, get label() { return i18n.t("components:yeniFirma.dekorator"); } },
  { id: FIRMA_TYPE.TASERON, get label() { return i18n.t("components:yeniFirma.taseron"); } },
];

/**
 * @param {number}   defaultRoleId  fuara eşlerken önerilecek rol (açan alanın rolü)
 * @param {function} onCreated      (firma) => void — {firmaId, firmaName, firmaShortname}
 * @param {boolean}  showEsle       false → "fuara eşle" bölümü gizli; eşlemeyi çağıran yapar
 *                                  (Yeni Kişi akışı: stand/taşeron eşlemesi ayrı popup'ta)
 */
export default function CreateFirmaModal({
  isOpen,
  onClose,
  onCreated,
  fuarId,
  defaultRoleId = FIRMA_TYPE.KATILIMCI,
  defaultName = "",
  forceLive = false,
  showEsle = true,
}) {
  const { t } = useTranslation("components");
  const [firmaName, setFirmaName] = useState(defaultName);
  const [firmaShortname, setFirmaShortname] = useState("");
  const [isYabanci, setIsYabanci] = useState(false);
  // #554: ülke adıyla seçilir (UlkeSecici); state sayısal country id'si. WAS: String(UNKNOWN_COUNTRY_ID) + "Ülke Kodu" sayı kutusu.
  const [countryId, setCountryId] = useState(UNKNOWN_COUNTRY_ID);
  // #197: opsiyonel. Girilmezse firma "vergi numarası girilmedi" olarak açılır; Kayıt Ayarları açıksa
  // (#198) firma kullanıcısı o zaman personel işlemi yapamaz.
  const [taxNumber, setTaxNumber] = useState("");
  const [esle, setEsle] = useState(showEsle);
  const [roleId, setRoleId] = useState(String(defaultRoleId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Firma yazıldıktan SONRA bir adım patlarsa bunu doldurup formu kilitliyoruz —
  // "Tekrar Dene" ikinci bir firma kaydı yaratırdı (backend'de dedupe yok).
  const [partial, setPartial] = useState(null);

  const trimmedName = firmaName.trim();
  const nameTooLong = trimmedName.length > FIRMA_NAME_MAX;
  const taxNumberError = vergiNoHatasi(taxNumber, !isYabanci);
  // WAS (backlog #197 öncesi): const valid = trimmedName !== "" && !nameTooLong && (!isYabanci || countryId !== "");
  // WAS (#554 öncesi): ... && (!isYabanci || countryId !== "") && ... — seçici hep bir değer taşır (Bilinmiyor = 0).
  const valid = trimmedName !== "" && !nameTooLong && !taxNumberError;

  async function handleSubmit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);

    let created;
    try {
      created = await createFirma(
        {
          firmaName: trimmedName,
          firmaShortname: firmaShortname.trim() || null,
          // Türkiye seçiliyse backend firmaNation'ı 1'e zorluyor; burada da öyle.
          firmaNation: isYabanci ? FIRMA_NATION.YABANCI : FIRMA_NATION.YERLI,
          countryId: isYabanci ? Number(countryId) : TURKIYE_COUNTRY_ID,
          taxNumber: vergiNoTemizle(taxNumber) || null,   // #197
        },
        { forceLive }
      );
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("yeniFirma.firmaOlusturulamadi"));
      setSubmitting(false);
      return;
    }

    if (!created?.firmaId) {
      // Yazıldı ama id okunamadı. Kullanıcı firmayı arama kutusundan elle seçsin.
      setPartial(t("yeniFirma.firmaOlusturulduAncakYeniId"));
      setSubmitting(false);
      return;
    }

    if (esle && fuarId) {
      try {
        await esleFirmaToFuar(
          { firmaId: created.firmaId, fuarId, firmaTypeId: Number(roleId) },
          { forceLive }
        );
      } catch (e) {
        // Firma var, fuar bağlantısı yok. Seçim yine de geçerli — standa atama
        // firma_to_stand üzerinden yürür; eksik olan yalnızca fuarın firma listesi.
        setPartial(
          t("yeniFirma.firmaOlusturulduAncakFuaraEsleme", { firmaId: created.firmaId, v: e?.response?.data?.message || e?.message || "bilinmeyen hata" })
        );
        setSubmitting(false);
        onCreated?.(created);
        return;
      }
    }

    setSubmitting(false);
    onCreated?.(created);
  }

  return (
    <Modal isOpen={isOpen} toggle={submitting ? undefined : onClose} centered>
      <ModalHeader toggle={submitting ? undefined : onClose}>
        <Building2 size={16} className="me-2" />{t("yeniFirma.yeniFirma")}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="py-2">{error}</Alert>}
        {partial && <Alert color="warning" className="py-2">{partial}</Alert>}

        <Row className="g-3">
          <Col md={12}>
            <FormGroup className="mb-0">
              <Label size="sm">{t("yeniFirma.firmaUnvani")}{" "}<span className="text-danger">*</span></Label>
              <Input
                bsSize="sm"
                value={firmaName}
                maxLength={FIRMA_NAME_MAX}
                invalid={nameTooLong}
                disabled={submitting || !!partial}
                placeholder="Coca Cola Corporation"
                onChange={(e) => setFirmaName(e.target.value)}
              />
              <FormText>{t("yeniFirma.tuzelUnvanSozlesmelerdeGecenTam")}</FormText>
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup className="mb-0">
              <Label size="sm">{t("yeniFirma.kisaAd")}{" "}<span className="text-muted fw-normal">{t("yeniFirma.opsiyonel")}</span></Label>
              <Input
                bsSize="sm"
                value={firmaShortname}
                disabled={submitting || !!partial}
                placeholder="Coca Cola"
                onChange={(e) => setFirmaShortname(e.target.value)}
              />
              {/* WAS (#208 öncesi): Standın tabela adı DEĞİL — o stand başına girilir. */}
              <FormText>{t("yeniFirma.listelerdeGorunenKisaAdStandin")}</FormText>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup className="mb-0">
              <Label size="sm">{t("yeniFirma.uyruk")}</Label>
              <Input
                type="select" bsSize="sm"
                value={isYabanci ? "2" : "1"}
                disabled={submitting || !!partial}
                onChange={(e) => {
                  const yabanci = e.target.value === "2";
                  setIsYabanci(yabanci);
                  setCountryId((c) => uyrukDegisinceUlke(c, yabanci ? FIRMA_NATION.YABANCI : FIRMA_NATION.YERLI));
                }}
              >
                <option value="1">{t("yeniFirma.yerliTurkiye")}</option>
                <option value="2">{t("yeniFirma.yabanci")}</option>
              </Input>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup className="mb-0">
              <Label size="sm" for="yeni-firma-ulke">{t("yeniFirma.ulke")}</Label>
              {/* WAS (#554 öncesi): "Ülke Kodu" sayı kutusu (country tablosunun id'si). Yerli: Türkiye kilitli; Yabancı: Türkiye dışı. */}
              <UlkeSecici
                inputId="yeni-firma-ulke"
                value={isYabanci ? countryId : TURKIYE_COUNTRY_ID}
                onChange={setCountryId}
                turkiyeHaric
                disabled={!isYabanci || submitting || !!partial}
              />
              <FormText>
                {isYabanci
                  ? t("yeniFirma.yabanciUlkeIpucu")
                  : t("yeniFirma.yerliUlkeIpucu")}
              </FormText>
            </FormGroup>
          </Col>

          {/* #197: Vergi Numarası. Yerli: 10 hane VKN / 11 hane TCKN; Yabancı: serbest. */}
          <Col md={12}>
            <FormGroup className="mb-0">
              <Label size="sm">{t("yeniFirma.vergiNumarasi")}{" "}<span className="text-muted fw-normal">{t("yeniFirma.opsiyonel")}</span></Label>
              <Input
                bsSize="sm"
                value={taxNumber}
                maxLength={isYabanci ? VERGI_NO_MAX : 14}
                inputMode={isYabanci ? "text" : "numeric"}
                invalid={!!taxNumberError}
                disabled={submitting || !!partial}
                placeholder={isYabanci ? t("yeniFirma.yabanciVergiKimlikNo") : "1234567890"}
                onChange={(e) => setTaxNumber(e.target.value)}
              />
              {taxNumberError ? (
                <div className="invalid-feedback d-block">{taxNumberError}</div>
              ) : (
                <FormText>
                  {isYabanci
                    ? t("yeniFirma.ulkesindekiVergiKimlikNumarasi")
                    : t("yeniFirma.n10HaneliVknSahisSirketiyse")}{" "}
                  {/* #435: bir Yabancı firma vergi numarası borçlu değildir (backend #434): "personel işlemleri bekler" cümlesi yalnızca Yerli firma için */}
                  {!isYabanci && t("yeniFirma.girilmezseFirmaPersonelIslemleriIcin")}
                </FormText>
              )}
            </FormGroup>
          </Col>
        </Row>

        {showEsle && (<>
        <hr />

        <FormGroup check className="mb-2">
          <Input
            type="checkbox" id="createFirmaEsle"
            checked={esle}
            disabled={!fuarId || submitting || !!partial}
            onChange={(e) => setEsle(e.target.checked)}
          />
          <Label check for="createFirmaEsle" className="small">
            {t("yeniFirma.buFuaraDaEkleEsle")}
          </Label>
        </FormGroup>
        {esle && (
          <FormGroup className="mb-0">
            <Label size="sm">{t("yeniFirma.fuardakiRolu")}</Label>
            <Input
              type="select" bsSize="sm" value={roleId}
              disabled={submitting || !!partial}
              onChange={(e) => setRoleId(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </Input>
            <FormText>
              {t("yeniFirma.eslemeFirmayiFuaraBaglarStanda")}
            </FormText>
          </FormGroup>
        )}
        {!esle && (
          <div className="text-muted small">
            {t("yeniFirma.eslemezsenizFirmaStandaAtanabilirAma")}
          </div>
        )}
        </>)}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={submitting}>
          {partial ? t("common:kapat") : t("yeniFirma.vazgec")}
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={!valid || submitting || !!partial}>
          {submitting ? <Spinner size="sm" className="me-1" /> : <Plus size={14} className="me-1" />}
          {t("yeniFirma.firmayiOlustur")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
